/**
 *  Binding is an object that binds a particular key combination to a specific action.  A binding object is retrieved via the
 *  <code>getBinding()</code> API of the KeyBindings service.<br><br>
 *  Actions can be assigned to a binding via the <code>setAction()</code> API according to the following rules:<br>
 *  1. An action can be set on a binding, but only for a specific css selector.<br>
 *  2. A selector for a binding can have only one action assigned to it.<br>
 *  3. A binding can have more than action assigned to it as long as each action is assigned a different selector.<br>
 *  4. The same action can be assigned to the same binding as long as they are assigned to different css selectors.<br>
 *  5. The same action can be assigned to the same selector on different bindings.<br><br>
 *  When assigning an action to a binding, the API allows the developer to determine how stopPropagation and preventDefault
 *  is handled for the event by passing in an optional argument to the <code>setAction()</code> API.
 *  By default, stopPropagation and preventDefault is set to true.
 * @module mw-keybindings/Binding
 *
 * @copyright Copyright 2016 The MathWorks, Inc.
 */

define([
    // dojo
    "dojo/_base/declare",
    "dojo/_base/lang",
    // keybindings
    "mw-keybindings/BindingUtils"

], function (declare, lang, BindingUtils) {
    return declare(null, {

        /**
         * Constructor for a binding
         * @param {array} keyCombination - An array of keys making up a key binding as defined by the <code>KeyBindings.Keys</code> object.
         * @param {object} option - An object to set options on the binding. Currently the only valid option is an object with a <code>platform</code> property to
         * set the platform the bindings is to run on. The valid choices for the platform property are defined in the <code>KeyBindings.Platform</code> object.
         * @param {actionDataService} actionDataService - An instance of an actionDataService to be registered with this service.  It must contain the actions that will be set in
         * the <code>setAction</code> API on the <code>Binding</code> object returned by this service.
         *
         * @instance
         */
        constructor: function (keyCombination, option, actionDataService) {

            // store the actionDataService
            this._actionDataService = actionDataService;

            // get the binding data for this key sequence
            this._keyBindingData = BindingUtils.getKeyBindingData(keyCombination, option);

            // create a map to store the selector and its action
            this._selectorActionMap = {};
        },

        /**
         * Sets an action on the binding for a particular selector. A binding can have multiple actions
         * bound to them provided they are for different selectors
         * @param {} selector - A css selector
         * @param {string} actionId - The id of the action in the actionDataService
         * @param {object} options - An object with <code>stopPropagation</code> and <code>preventDefault</code> properties.<br>
         *  - stopPropagation prevents the event from bubbling up the DOM tree, preventing any parent handlers from being notified of the event;<br>
         *  - preventDefault prevents the default action of the event from being triggered. <br>Valid values for these properties are true or false; the default value is true.
         *
         * @instance
         */
        setAction: function (selector, actionId, options) {

            try {
                document.querySelector(selector);
            } catch (e) {
                throw new Error("Invalid selector for key binding: " + selector);
            }

            // verify there is not an action already on this binding for that selector
            if (this._selectorActionMap[selector] !== undefined) {
                throw new Error("An action already exists on this binding for the following selector: " +
                    selector);
            }

            // retrieve the action from the data service and verify that an action with the
            // given id exists in the ActionDataService
            var action = this._actionDataService.getAction(actionId);
            if (action === null) {
                throw new Error("Cannot assign the action to the binding.  No action exists in the ActionDataService with the following id: " +
                    actionId);
            }

            // got this far so no errors
            // add the action to the selectorAction map
            this._selectorActionMap[selector] = {
                actionId: actionId,
                preventDefault: (options && options.hasOwnProperty('preventDefault')) ?
                    options.preventDefault : true,
                stopPropagation: (options && options.hasOwnProperty('stopPropagation')) ?
                    options.stopPropagation : true
            };

            // update the shortcut on the action
            // g1350541 - The binding should not assume the actions always have a shortcut property
            var actionShortcut = action.shortcut || "";
            var updatedShortcut = actionShortcut.concat("," + this.getShortcut());
            if (updatedShortcut[0] === ",") {
                updatedShortcut = updatedShortcut.slice(1, updatedShortcut.length)
            }

            this._actionDataService.updateAction(actionId,
                {shortcut: updatedShortcut});
        },

        /**
         * Remove the action for the given selector
         * @param {} selector - A css selector
         *
         * @instance
         */
        removeAction: function (selector) {

            // error check
            if (this._selectorActionMap[selector] === undefined) {
                throw new Error("No such action exists on this binding for the following selector: " +
                    selector);
            }

            // get the action's shortcut
            var actionInfo = this._selectorActionMap[selector];
            var action = this._actionDataService.getAction(actionInfo.actionId);
            var actionShortcut = action.shortcut;

            // remove the binding's shortcut from the action.  First get the binding shortcut
            var bindingShortcut = this.getShortcut();

            // remove in case the binding shortcut is in the middle or end of the action shortcut string.  There
            // will be an "," before it
            var updatedShortcut = actionShortcut.replace("," + bindingShortcut, "");

            // remove in case the binding shortcut is at the beginning of the action shortcut string, and there is
            // more text after..  There will be an "," after it
            updatedShortcut = updatedShortcut.replace(bindingShortcut + ",", "");

            // remove in case the binding shortcut is the only thing in the action shortcut...
            updatedShortcut = updatedShortcut.replace(bindingShortcut, "");

            this._actionDataService.updateAction(actionInfo.actionId,
                {shortcut: updatedShortcut.toString()});

            // remove the selector from the selectorMap
            delete this._selectorActionMap[selector];
        },

        /**
         * Destroy the binding
         *
         * @instance
         */
        cleanup: function () {

            // todo: remove the keyHandling and listening for this binding over all selectors.

            // remove the shortcut from all actions
            // get the new shortcut
            var bindingShortcut = this.getShortcut();

            // loop over the selectorMap, and for each action, update the shortcut string with the new shortcut
            for (var key in this._selectorActionMap) {
                var actionId = this._selectorActionMap[key].actionId;
                var action = this._actionDataService.getAction(actionId);
                var actionShortcut = action.shortcut;

                // remove in case the binding shortcut is in the middle or end of the action shortcut string.  There
                // will be an "," before it
                var updatedShortcut = actionShortcut.replace("," + bindingShortcut, "");

                // remove in case the binding shortcut is at the beginning of the action shortcut string, and there is
                // more text after..  There will be an "," after it
                updatedShortcut = updatedShortcut.replace(bindingShortcut + ",", "");

                // remove in case the binding shortcut is the only thing in the action shortcut...
                updatedShortcut = updatedShortcut.replace(bindingShortcut, "");

                this._actionDataService.updateAction(actionId,
                    {shortcut: updatedShortcut.toString()});
            }
        },

        /**
         * Get the shortcut for this binding
         * @returns {string} The binding shortcut
         *
         * @instance
         */
        getShortcut: function () {
            return this._keyBindingData.shortcut;
        },

        /**
         * Get the key sequence identifier for this binding
         * @returns {*}
         *
         * @private
         */
        getKeySequenceIdentifier: function () {
            return this._keyBindingData.keySequenceIdentifier;
        },

        /**
         * Executes the Action of the actionId
         *
         * @param actionId
         *
         * @private
         */
        executeAction: function (actionId) {
            this._actionDataService.executeAction(actionId);
        },

        /**
         * Get the ActionInfo { actionId, stopPropagation, preventDefault} for the
         * selector that matches the currentNode
         *
         * @param currentNode
         * @returns {*}
         *
         * @private
         */
        getActionInfoForNode: function (currentNode) {
            var _selector;
            var selectorList = Object.keys(this._selectorActionMap);
            selectorList.some(lang.hitch(this, function (selector) {
                // The currentNode is checked for a match with each of the selectors in the
                // selectorList.
                var doesNodeMatchSelector = this._matches(currentNode, selector);
                if (doesNodeMatchSelector) {
                    _selector = selector;
                    return;
                }
            }));
            return this._selectorActionMap[_selector];
        },

        /**
         *  As matches() does not work in all browsers like IE11, we create function that does the
         *  same behavior of checking if the selector matches with the element passed in.
         *  (g1387116)
         * @param element - element that is passed in to check if it matches the given selector
         * @param selector  - String that that is a valid selector e.g "#id", " div.abcClass"
         * @returns {boolean} - true if it is a match
         *
         * @private
         */
        _matches: function (element, selector) {
            var matches = (element.document || element.ownerDocument).querySelectorAll(selector),
                i = matches.length;
            while (--i >= 0 && matches.item(i) !== element) {}
            return i > -1;
        }
    });
});

